import React, { useState } from 'react';
import { MessageCircleIcon, MicIcon, GlobeIcon, ListChecksIcon, TrendingUpIcon, PlusIcon, MinusIcon, CheckIcon } from 'lucide-react';
import { StepHeading, AiButton } from '../SettingsUI';
import { ShopConfig, AiTone, AiLanguage, AI_CAPABILITIES } from '../../../data/shopConfig';
import { resolveTheme } from '../../../utils/theme';
import { RECO_STYLES } from '../../../data/sellingRecommendations';
import { setStrategy, useSelling } from '../../../data/sellingRules';
import { DesignSection, ControlLabel, Segmented, SwitchRow } from '../design/DesignControls';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
  /** Rendered inside the Shop Studio panel — narrower, no big header. */
  compact?: boolean;
}
type SectionId = 'voice' | 'language' | 'abilities' | 'selling';
const GREETINGS: Record<AiTone, string> = {
  Professional: `Welcome to {name}. How can I help you find the right option today?`,
  Friendly: `Hi there! Looking for something specific? I'm happy to help.`,
  Premium: `Welcome to {name}. I'd be delighted to help you choose.`,
  Casual: `Hey! What are you after today?`
};
/** Each tone shown as the words the customer would actually read. */
const TONE_SAMPLE: Record<AiTone, string> = {
  Professional: 'Certainly — here are three options.',
  Friendly: 'Great choice! Here are a few I love.',
  Premium: 'With pleasure. May I suggest these?',
  Casual: 'Sure thing, check these out.'
};
const LANGUAGE_NOTE: Record<AiLanguage, string> = {
  Automatic: 'Replies in whatever language the customer writes in.',
  English: 'Always replies in English.',
  Arabic: 'Always replies in Arabic.'
};
export function AiExperience({
  config,
  update,
  compact
}: Props) {
  const [open, setOpen] = useState<SectionId | null>(null);
  const [writing, setWriting] = useState(false);
  const {
    strategy
  } = useSelling();
  const t = resolveTheme(config.theme);
  const toggle = (id: SectionId) => setOpen(o => o === id ? null : id);
  const setAi = (patch: Partial<ShopConfig['ai']>) => update({
    ai: {
      ...config.ai,
      ...patch
    }
  });
  const generateGreeting = () => {
    setWriting(true);
    window.setTimeout(() => {
      setAi({
        greeting: GREETINGS[config.ai.tone].replace('{name}', config.publicName || 'our shop')
      });
      setWriting(false);
    }, 800);
  };
  const onCount = AI_CAPABILITIES.filter(c => config.ai.capabilities[c]).length;
  return <div className="space-y-3" data-id="element-3739" data-mp="2j04qb6xd9z1j">
      {compact ? <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-3740" data-mp="2ny66nk3erk9b">
          Shape how your assistant talks to customers.
        </p> : <StepHeading title="Personalize your AI" desc="Shape how your assistant talks to customers. You can change this whenever you like." data-id="element-3741" data-mp="29zopk580d60d" />}

      {/* Voice — tone and the opening line, previewed together */}
      <DesignSection icon={MicIcon} title="Voice & greeting" summary={`${config.ai.tone} tone`} open={open === 'voice'} onToggle={() => toggle('voice')} data-id="element-3742" data-mp="d40nbsdcwv4u">
        <div data-id="element-3743" data-mp="iayp6gxyp6ts">
          <ControlLabel data-id="element-3744" data-mp="ibgvy7i933fy">How it sounds</ControlLabel>
          <div role="radiogroup" className="grid grid-cols-2 gap-2" data-id="element-3745" data-mp="w127wxxmuieh">
            {(Object.keys(TONE_SAMPLE) as AiTone[]).map(tone => {
            const on = config.ai.tone === tone;
            return <button key={tone} role="radio" aria-checked={on} onClick={() => setAi({
              tone
            })} className={`relative text-left p-3 rounded-xl transition-shadow duration-150 ease-out ${on ? 'ring-2 ring-primary-indigo bg-primary-indigo/[0.03]' : 'ring-1 ring-slate-200 hover:ring-slate-300'}`} data-id="element-3746" data-mp="15yypa7h5y9l1">
                  <span className="flex items-center justify-between" data-id="element-3747" data-mp="3gqrll7mlny5l">
                    <span className="text-[13px] font-semibold text-dark-navy" data-id="element-3748" data-mp="3cpxyuhkw8pea">{tone}</span>
                    {on && <span className="w-4 h-4 rounded-full bg-primary-indigo text-white flex items-center justify-center" data-id="element-3749" data-mp="3e7wkmxjlxbf3">
                        <CheckIcon className="w-2.5 h-2.5" strokeWidth={3.5} data-id="element-3750" data-mp="3j4vav505gy80" />
                      </span>}
                  </span>
                  <span className="block text-[12px] text-slate-500 mt-1 leading-snug italic" data-id="element-3751" data-mp="3s6wc0cyi84ey">
                    “{TONE_SAMPLE[tone]}”
                  </span>
                </button>;
          })}
          </div>
        </div>

        <div data-id="element-3752" data-mp="2zj0ex750wahn">
          <div className="flex items-center justify-between gap-3 mb-2.5" data-id="element-3753" data-mp="2ziqqhnzmmbe2">
            <p className="text-[13px] font-semibold text-dark-navy" data-id="element-3754" data-mp="34g8x37q68eiw">First message</p>
            <AiButton onClick={generateGreeting} label="Write it for me" busy={writing} data-id="element-3755" data-mp="398a6mlvyfr58" />
          </div>
          <textarea rows={2} value={config.ai.greeting} onChange={e => setAi({
          greeting: e.target.value
        })} className="w-full px-3.5 py-2.5 bg-white ring-1 ring-slate-200 rounded-xl text-sm text-dark-navy outline-none focus:ring-2 focus:ring-primary-indigo/40 resize-none transition-shadow duration-150 ease-out" data-id="element-3756" data-mp="2btee6f905n7j" />
          {/* In the shop's own colours */}
          <div className="mt-3 p-3 rounded-xl bg-slate-50 flex items-start gap-2.5" data-id="element-3757" data-mp="1z8pot8d6e86y">
            <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{
            background: t.primary,
            color: t.primaryText
          }} data-id="element-3758" data-mp="1v7duipjcwysf">
              <MessageCircleIcon className="w-3.5 h-3.5" data-id="element-3759" data-mp="1lhktgiguc22f" />
            </span>
            <p className="px-3.5 py-2 bg-white rounded-2xl rounded-tl-md text-[13px] text-slate-700 shadow-sm" data-id="element-3760" data-mp="1gizs2fpurcce">
              {config.ai.greeting || 'Hi! How can I help you today?'}
            </p>
          </div>
        </div>
      </DesignSection>

      {/* Language */}
      <DesignSection icon={GlobeIcon} title="Reply language" summary={config.ai.language === 'Automatic' ? 'Matches the customer' : config.ai.language} open={open === 'language'} onToggle={() => toggle('language')} data-id="element-3761" data-mp="17knu1smnpc9x">
        <div className="space-y-2.5" data-id="element-3762" data-mp="wyiut389yitw">
          <Segmented<AiLanguage> label="Reply language" value={config.ai.language} onChange={language => setAi({
          language
        })} options={[{
          id: 'Automatic',
          label: 'Automatic'
        }, {
          id: 'English',
          label: 'English'
        }, {
          id: 'Arabic',
          label: 'العربية'
        }]} data-id="element-3763" data-mp="wveitjokiiso" />
          <p className="text-[12.5px] text-slate-500" data-id="element-3764" data-mp="1gl6wpzujds1c">{LANGUAGE_NOTE[config.ai.language]}</p>
        </div>
      </DesignSection>

      {/* Capabilities */}
      <DesignSection icon={ListChecksIcon} title="What it can help with" summary={`${onCount} of ${AI_CAPABILITIES.length} switched on`} open={open === 'abilities'} onToggle={() => toggle('abilities')} data-id="element-3765" data-mp="16vdn34dyfmzq">
        <div className="space-y-4" data-id="element-3766" data-mp="1zdj1et8nx5en">
          {AI_CAPABILITIES.map(c => <SwitchRow key={c} title={c} checked={config.ai.capabilities[c] ?? false} onChange={v => setAi({
          capabilities: {
            ...config.ai.capabilities,
            [c]: v
          }
        })} data-id="element-3767" data-mp="3k1by8x31v5y3" />)}
        </div>
        <p className="text-[12px] text-slate-400 leading-relaxed" data-id="element-3768" data-mp="3ostp2sbczq5c">
          It only uses your own catalogue and details — it never invents prices or stock.
        </p>
      </DesignSection>

      {/* Selling */}
      <DesignSection icon={TrendingUpIcon} title="Recommending products" summary={`${strategy.style} · up to ${strategy.max} per chat`} open={open === 'selling'} onToggle={() => toggle('selling')} data-id="element-3769" data-mp="2w5g1vlxgq8yf">
        <div data-id="element-3770" data-mp="357gwsc584iwx">
          <ControlLabel data-id="element-3771" data-mp="357p8w1tivk72">How often it suggests things</ControlLabel>
          <Segmented<string> label="Recommendation style" value={strategy.style} onChange={style => setStrategy({
          style: style as typeof strategy.style
        })} options={RECO_STYLES.map(s => ({
          id: s.id,
          label: s.id
        }))} data-id="element-3772" data-mp="pcncb5x8bwki" />
          <p className="text-[12.5px] text-slate-500 mt-2 leading-snug" data-id="element-3773" data-mp="19he3tfh3id1d">
            {RECO_STYLES.find(s => s.id === strategy.style)?.help}
            {strategy.style === 'Balanced' && ' Recommended.'}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3" data-id="element-3774" data-mp="1iar5p6x50ozl">
          <div data-id="element-3775" data-mp="1n6od2nqfal08">
            <p className="text-[13px] font-semibold text-dark-navy" data-id="element-3776" data-mp="1n77qps91in8g">Most suggestions per chat</p>
            <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-3777" data-mp="1s59cwm1rj2o5">So customers never feel pushed.</p>
          </div>
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-xl p-1 flex-shrink-0" data-id="element-3778" data-mp="1x6ouu58n8t2b">
            <button onClick={() => setStrategy({
            max: Math.max(1, strategy.max - 1)
          })} aria-label="Fewer" className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white transition-colors duration-150 ease-out" data-id="element-3779" data-mp="216plco90oupq">
              <MinusIcon className="w-3.5 h-3.5" data-id="element-3780" data-mp="3vgnq414e17kz" />
            </button>
            <span className="w-7 text-center text-sm font-bold text-dark-navy tabular-nums" data-id="element-3781" data-mp="3qnl5iihl98gx">
              {strategy.max}
            </span>
            <button onClick={() => setStrategy({
            max: Math.min(5, strategy.max + 1)
          })} aria-label="More" className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white transition-colors duration-150 ease-out" data-id="element-3782" data-mp="3lngq6vsf2jsa">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-3783" data-mp="d7n666yvmq6h" />
            </button>
          </div>
        </div>

        <div className="space-y-4" data-id="element-3784" data-mp="9a8ywkurash4">
          <SwitchRow title="Suggest a better option" desc="e.g. a larger size or upgraded model" checked={strategy.better} onChange={better => setStrategy({
          better
        })} data-id="element-3785" data-mp="49kyx9ojv1ar" />
          <SwitchRow title="Suggest useful extras" desc="e.g. delivery, add-ons or matching items" checked={strategy.addons} onChange={addons => setStrategy({
          addons
        })} data-id="element-3786" data-mp="2oyhgdcc6p9xz" />
        </div>
        <p className="text-[12px] text-slate-400 leading-relaxed" data-id="element-3787" data-mp="3jesk6igzu97d">
          What to suggest is set per category in your Catalogue.
        </p>
      </DesignSection>
    </div>;
}